/**
 * Natural sort comparison function that sorts strings with numbers intelligently.
 * Sorts alphanumeric strings where numeric parts are compared as numbers.
 *
 * Examples:
 * - ["A1", "A10", "A2"] => ["A1", "A2", "A10"]
 * - ["B-1", "B-10", "B-2"] => ["B-1", "B-2", "B-10"]
 * - ["Tote1", "Tote10", "Tote2"] => ["Tote1", "Tote2", "Tote10"]
 */
export function naturalSort(a: string, b: string): number {
  // Split strings into parts (alternating text and numbers)
  const aParts = a.match(/(\d+|\D+)/g) || [];
  const bParts = b.match(/(\d+|\D+)/g) || [];

  const maxLength = Math.max(aParts.length, bParts.length);

  for (let i = 0; i < maxLength; i++) {
    const aPart = aParts[i] || "";
    const bPart = bParts[i] || "";

    // Check if both parts are numeric
    const aIsNum = /^\d+$/.test(aPart);
    const bIsNum = /^\d+$/.test(bPart);

    if (aIsNum && bIsNum) {
      // Compare as numbers
      const aNum = parseInt(aPart, 10);
      const bNum = parseInt(bPart, 10);

      if (aNum !== bNum) {
        return aNum - bNum;
      }

      // If numeric values are equal but string representations differ (e.g., leading zeros)
      // Compare by string length first (fewer leading zeros comes first)
      if (aPart.length !== bPart.length) {
        return aPart.length - bPart.length;
      }
    } else {
      // Compare as strings (case-insensitive)
      const comparison = aPart.localeCompare(bPart, undefined, {
        sensitivity: "base",
      });
      if (comparison !== 0) {
        return comparison;
      }
    }
  }

  return 0;
}

/**
 * Sorts an array of strings using natural sort order.
 * Returns a new sorted array without modifying the original.
 */
export function naturalSortArray(items: string[]): string[] {
  return [...items].sort(naturalSort);
}
